import React from 'react';
import { NavLink } from 'react-router-dom';
import { useClerk } from '@clerk/clerk-react';
import { sharedStyles } from '../styles/sharedStyles';
import { useUser } from '../hooks/useUser';

const Sidebar = ({ isOpen = true, onClose }) => {
  const { signOut } = useClerk();
  const { user } = useUser();

  const menuItems = [
    { path: '/', icon: 'bi-speedometer2', label: 'Dashboard' },
    { path: '/employees', icon: 'bi-people', label: 'Operators' },
    { path: '/machines', icon: 'bi-truck', label: 'Machines' },
    { path: '/customers', icon: 'bi-building', label: 'Customers' },
    { path: '/jobs', icon: 'bi-briefcase', label: 'Jobs' },
    { path: '/tickets', icon: 'bi-receipt', label: 'Tickets' },
    { path: '/schedule', icon: 'bi-calendar3', label: 'Schedule', roles: ['admin', 'foreman'] },
    { path: '/ticket-reports', icon: 'bi-file-earmark-text', label: 'Ticket Reports', roles: ['admin', 'operator'] },
    { path: '/users', icon: 'bi-person-badge', label: 'Users', roles: ['admin'] }
  ];

  const visibleMenuItems = menuItems.filter(
    (item) => !item.roles || item.roles.includes(user?.role)
  );

  const sidebarStyle = {
    width: '280px',
    height: '100vh',
    backgroundColor: '#ffffff',
    borderRight: '1px solid #e5e9f0',
    position: 'fixed',
    left: isOpen ? 0 : '-280px',
    top: 0,
    padding: '1.5rem 1rem',
    display: 'flex',
    flexDirection: 'column',
    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
    transition: 'left 0.3s cubic-bezier(0.4,0,0.2,1)',
    zIndex: 1200,
    overflowY: 'auto',
    overflowX: 'hidden'
  };

  // Hamburger button style
  const hamburgerStyle = {
    position: 'fixed',
    top: '1.5rem',
    left: '1.5rem',
    zIndex: 1300,
    background: '#fff',
    border: '1px solid #e5e9f0',
    borderRadius: '8px',
    width: '40px',
    height: '40px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
    cursor: 'pointer',
    fontSize: '1.5rem',
    transition: 'background 0.2s',
    // Only show on small screens
    display: 'none',
  };

  // Responsive: show hamburger on small screens
  React.useEffect(() => {
    const handleResize = () => {
      const btn = document.getElementById('sidebar-hamburger');
      if (btn) {
        btn.style.display = window.innerWidth < 992 ? 'flex' : 'none';
      }
    };
    window.addEventListener('resize', handleResize);
    handleResize();
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const logoStyle = {
    fontSize: '1.5rem',
    fontWeight: '700',
    color: sharedStyles.colors.primary.main,
    marginBottom: '2rem',
    padding: '0 1rem',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '100%',
    letterSpacing: '-0.02em'
  };

  const navStyle = {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.5rem'
  };

  const linkStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: '0.75rem',
    padding: '0.875rem 1rem',
    borderRadius: '12px',
    color: sharedStyles.colors.text.secondary,
    textDecoration: 'none',
    fontSize: '0.9375rem',
    fontWeight: '500',
    transition: 'all 0.2s ease-in-out',
    '&:hover': {
      backgroundColor: sharedStyles.colors.primary.light,
      color: sharedStyles.colors.primary.main
    }
  };

  const activeLinkStyle = {
    ...linkStyle,
    backgroundColor: sharedStyles.colors.primary.light,
    color: sharedStyles.colors.primary.main,
    fontWeight: '600'
  };

  const iconStyle = {
    fontSize: '1.25rem',
    width: '1.5rem',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  };

  const logoutButtonStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: '0.75rem',
    padding: '0.75rem 1rem',
    borderRadius: '12px',
    color: sharedStyles.colors.danger.main,
    textDecoration: 'none',
    fontSize: '0.9375rem',
    fontWeight: '500',
    transition: 'all 0.2s ease-in-out',
    cursor: 'pointer',
    border: 'none',
    backgroundColor: 'transparent',
    width: '100%',
    textAlign: 'left',
    '&:hover': {
      backgroundColor: `${sharedStyles.colors.danger.main}15`,
    }
  };

  const footerStyle = {
    marginTop: 'auto',
    padding: '1rem',
    borderTop: '1px solid #e5e9f0',
    fontSize: '0.875rem',
    color: sharedStyles.colors.text.secondary,
    textAlign: 'center'
  };

  return (
    <>
      <div
        id="sidebar-hamburger"
        className="print-hidden"
        style={hamburgerStyle}
        onClick={onClose}
        aria-label="Toggle sidebar"
      >
        <i className="bi bi-list"></i>
      </div>
      <div className="print-hidden" style={sidebarStyle}>
        <div style={logoStyle}>
          <img 
            src="/file.svg" 
            alt="Griffin Landworks Logo" 
            style={{ 
              height: '80px',
              width: 'auto',
              maxWidth: '200px',
              objectFit: 'contain',
              marginBottom: 0,
              display: 'block'
            }} 
          />
        </div>

        <nav style={navStyle}>
          {visibleMenuItems.map((item) => (
            <NavLink
              key={item.path}
              to={item.path}
              style={({ isActive }) => isActive ? activeLinkStyle : linkStyle}
            >
              <i className={`bi ${item.icon}`} style={iconStyle}></i>
              {item.label}
            </NavLink>
          ))}
        </nav>

        <div style={footerStyle}>
          <NavLink
            to="/profile"
            style={({ isActive }) => isActive ? activeLinkStyle : linkStyle}
          >
            <i className="bi bi-person" style={iconStyle}></i>
            Profile
          </NavLink>
          <button 
            onClick={() => signOut()}
            style={logoutButtonStyle}
            aria-label="Sign out"
          >
            <i className="bi bi-box-arrow-right" style={iconStyle}></i>
            Sign Out
          </button>
          <div style={{ marginTop: '1rem', marginBottom: '0.5rem', fontWeight: '500' }}>
          Griffin Landworks
          </div>
          <div style={{ fontSize: '0.75rem', color: sharedStyles.colors.text.disabled }}>
            Version 1.0.0
          </div>
        </div>
      </div>
    </>
  );
};

export default Sidebar; 